/* UI-DESIGN: CaseMark is the abstraction layer over the puzzle set's mark
   (currently the Lotus). Components should import this — not Lotus directly —
   so a future puzzle theme (Sherlock, Bank Robber, etc.) can register its
   own icon here without touching every page.

   To add a theme-specific mark when a new puzzle set ships:
   1. Add the icon component to the ICONS registry below
   2. Make sure its slug matches the data-puzzle-theme attribute name
   The component reads document.documentElement.dataset.puzzleTheme on mount
   and renders the matching icon. Falls back to Lotus when no match. */
import { useSyncExternalStore } from "react";
import Lotus from "./Lotus";

type CaseMarkProps = { className?: string };

const ICONS: Record<string, (props: CaseMarkProps) => React.ReactElement> = {
  "white-lotus": Lotus,
  /* sherlock: Magnifier,  ← add when the Sherlock set ships */
};

const DEFAULT_THEME = "white-lotus";

/* UI-DESIGN: subscribe to the html element's data-puzzle-theme attribute
   so the icon updates live when a designer flips the theme in DevTools */
const subscribe = (callback: () => void) => {
  if (typeof window === "undefined") return () => undefined;
  const observer = new MutationObserver(callback);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-puzzle-theme"],
  });
  return () => observer.disconnect();
};

const getSnapshot = () => {
  if (typeof document === "undefined") return DEFAULT_THEME;
  return document.documentElement.dataset.puzzleTheme || DEFAULT_THEME;
};

const getServerSnapshot = () => DEFAULT_THEME;

const CaseMark = ({ className }: CaseMarkProps) => {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const Icon = ICONS[theme] ?? Lotus;
  return <Icon className={className} />;
};

export default CaseMark;
